import { useState } from "react";

import {
  Button,
  DataList,
  EmptyState,
  ErrorState,
  Page,
  Panel,
  Skeleton,
  Stack,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import { useCatalog, useRollup, type ActivitySummary } from "../lib/queries";

interface Bound {
  readonly id: string;
  readonly requirement: string;
  readonly activity_id: string;
  readonly min: number;
  readonly max: number;
}

/** What a store page derives from a published activity: its rollup and its position bounds. */
export function Listing() {
  const [activityId, setActivityId] = useState<string | undefined>(undefined);
  const catalog = useCatalog();
  const rollup = useRollup(activityId);

  if (catalog.isPending) {
    return (
      <Page title="Listing" eyebrow="Publisher">
        <Skeleton height={220} />
      </Page>
    );
  }

  if (catalog.isError) {
    return (
      <Page title="Listing" eyebrow="Publisher">
        <ErrorState
          headline="Catalog unavailable"
          onRetry={() => void catalog.refetch()}
          retryLabel="Retry"
        />
      </Page>
    );
  }

  const activities = catalog.data.activities ?? [];
  const bounds: Bound[] = (rollup.data?.position_bounds ?? []).map((one) => ({
    id: `${one.activity_id}:${one.requirement}`,
    requirement: one.requirement,
    activity_id: one.activity_id,
    min: one.min,
    max: one.max,
  }));

  return (
    <Page title="Listing" eyebrow="Publisher">
      <Panel title="Activity">
        <PagedTable
          rows={activities}
          pageSize={6}
          caption="Activities to list"
          emptyHeadline="Nothing published"
          placeholder="Find an activity"
          match={(row, needle) => `${row.name} ${row.activity_id}`.toLowerCase().includes(needle)}
          columns={[
            { key: "name", header: "Activity", emphasis: true },
            { key: "activity_id", header: "Id", mono: true },
            {
              key: "open",
              header: "",
              align: "right",
              width: "110px",
              render: (row: ActivitySummary) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setActivityId(row.activity_id);
                  }}
                >
                  Open
                </Button>
              ),
            },
          ]}
        />
      </Panel>

      <Panel title="Rollup" subtitle={activityId}>
        {activityId === undefined ? (
          <EmptyState bare headline="No activity opened" />
        ) : rollup.isPending ? (
          <Skeleton height={140} />
        ) : rollup.isError ? (
          <ErrorState
            headline="Rollup unavailable"
            onRetry={() => void rollup.refetch()}
            retryLabel="Retry"
          />
        ) : (
          <Stack gap={5}>
            <DataList
              items={[
                {
                  id: rollup.data.activity_id,
                  title: rollup.data.activity_id,
                  fields: [
                    {
                      label: "Canvases",
                      value: (rollup.data.canvases ?? []).join(", ") || "none",
                    },
                    {
                      label: "Required inputs",
                      value: (rollup.data.required_inputs ?? []).join(", ") || "none",
                    },
                    {
                      label: "Needs a machine",
                      value: rollup.data.needs_a_machine === true ? "yes" : "no",
                    },
                    {
                      label: "Configuration",
                      value: String(rollup.data.configuration_field_count ?? 0),
                    },
                  ],
                },
              ]}
            />
            <PagedTable
              rows={bounds}
              pageSize={6}
              caption="Position bounds"
              emptyHeadline="No position bounds"
              placeholder="Find a requirement"
              match={(row, needle) => row.requirement.toLowerCase().includes(needle)}
              columns={[
                { key: "requirement", header: "Requirement", emphasis: true, mono: true },
                { key: "min", header: "Min", numeric: true },
                { key: "max", header: "Max", numeric: true },
              ]}
            />
          </Stack>
        )}
      </Panel>
    </Page>
  );
}
